import { isPlatformServer } from '@angular/common';
import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import type {
  EmailAddressResource,
  OrganizationSuggestionResource,
  UserOrganizationInvitationResource,
} from '@clerk/types';
import { TenantDto, UpdateProfileBodyDto, UserDto } from '@nusign/picflow-backend-types';
import {
  Observable,
  catchError,
  combineLatest,
  firstValueFrom,
  forkJoin,
  from,
  lastValueFrom,
  map,
  of,
  switchMap,
  take,
  tap,
  throwError,
  timer,
} from 'rxjs';
import { AuthService } from '../auth/services/auth.service';
import { standardMemoryCacheExpiry } from '../consts';
import { GROWTHBOOK } from '../growthbook';
import { backoffRetry } from '../lib/backoff-retry';
import { getTenantGroups, getUserGroups } from '../lib/get-tenant-groups';
import { forkJoinSafe } from '../lib/safe-fork-join';
import { sleep } from '../lib/sleep';
import { IGNORE_ALL_ERRORS, IGNORE_AUTH_ERRORS } from '../network-interceptor';
import { PICFLOW_ANALYTICS } from '../picflow-analytics';
import { AppConfigService } from '../providers/config.service';
import { BrowserService } from './browser.service';
import { DataLoaderService } from './data-loader.service';
import { LoggedInAnalyticsService } from './loggedin-analytics.service';
import { MergeAccountService } from './merge-account.service';
import { ObservableDataLoaderService } from './observable-data-loader.service';
import { SharedWithMeTenantService } from './shared-with-me-tenant.service';
import { StateService } from './state.service';
import { StorageService } from './storage.service';
import { SubscriptionService } from './subscription.service';
import { TenantService } from './tenant.service';
import { ToastService } from './toast.service';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private growthBook = inject(GROWTHBOOK);
  private state = inject(StateService);
  private auth = inject(AuthService);
  private http = inject(HttpClient);
  private loader = inject(DataLoaderService);
  private observableLoader = inject(ObservableDataLoaderService);
  private config = inject(AppConfigService);
  private analyticsService = inject(PICFLOW_ANALYTICS);
  private loggedInAnalytics = inject(LoggedInAnalyticsService);
  private tenantService = inject(TenantService);
  private sharedWithMeTenantService = inject(SharedWithMeTenantService);
  private mergeAccountService = inject(MergeAccountService);
  private browserService = inject(BrowserService);
  private toasts = inject(ToastService);
  private platformId = inject(PLATFORM_ID);
  private storage = inject(StorageService);
  private subscriptionService = inject(SubscriptionService);

  lastFetchTime: number;

  private userWatcher = combineLatest([
    this.state.user$,
    this.subscriptionService.getSubscription(),
    this.state.activeTenant$,
  ]).subscribe(([user]) => {
    this.loggedInAnalytics.identify(user);
  });

  getUser(): Observable<UserDto> {
    if (isPlatformServer(this.platformId)) return of(undefined);
    const hasActiveTenant = this.state.activeTenant$?.value;
    const key = hasActiveTenant ? 'user-data_with_tenant' : 'user-data_without_tenant';
    return this.observableLoader.get(
      key,
      () => {
        return from(this.auth.getTokenData()).pipe(
          switchMap(tokenData => {
            this.clearUserCache();
            if (!tokenData) return of(undefined);
            return forkJoin([
              this.getProfileData().pipe(take(1)),
              from(this.tenantService.loadTenants()),
              this.sharedWithMeTenantService.loadSharedWithMeTenants().pipe(take(1)),
            ]).pipe(
              map(([user]) => {
                if (!user) return;
                this.state.user = user;
                this.state.user$.next(this.state.user);
                if (!this.state.user.first_name) this.state.user.first_name = '';
                if (!this.state.user.last_name) this.state.user.last_name = '';
                this.checkSignedUp(this.state.user);
                this.checkClickParams(this.state.user);
                this.mergeAccountService.autoMerge().subscribe();

                const activeTenant = this.state.activeTenant$?.value;

                this.growthBook.setAttributes({
                  ...this.growthBook.getAttributes(),
                  loggedIn: true,
                  userId: this.state.user.id,
                  tenantId: activeTenant?.id,
                  $groups: [...getTenantGroups(activeTenant), ...getUserGroups(this.state.user)],
                });

                return this.state.user;
              })
            );
          }),
          catchError(err => {
            if (!(err instanceof HttpErrorResponse)) return throwError(() => err);
            if (err.status !== 401 && err.status !== 403) return throwError(() => err);
            return of(undefined);
          })
        );
      },
      standardMemoryCacheExpiry,
      false
    );
  }

  getProfileData(tries = 0): Observable<UserDto> {
    return this.observableLoader.get('user_profile', () => {
      return this.http
        .get<{ user: UserDto }>(`${this.config.apiEndpoint}/profile/current_user`, {
          context: new HttpContext().set(IGNORE_AUTH_ERRORS, true),
        })
        .pipe(
          map(resp => resp.user),
          catchError(err => {
            if (!(err instanceof HttpErrorResponse)) return throwError(() => err);
            const userCreatedAgo = Date.now() - this.auth.clerk.user.createdAt.getTime();
            if (userCreatedAgo > 1000 * 60 * 5) {
              // if the user was created more than 5 minutes ago, we can assume that retrying won't help
              return throwError(() => err);
            }
            if (tries > 10) return throwError(() => err);
            return timer(250).pipe(switchMap(() => this.getProfileData(tries + 1)));
          })
        );
    });
  }

  private checkSignedUp(user: UserDto) {
    if (!user) return;
    if (user.questionnaire.signedup) return;
    user.questionnaire.signedup = true;
    if (user.created_at > 1669368370) {
      // Before this date, signup was reported without updating the questionnaire so we don't want to report it twice
      const provider = this.auth.clerk.user?.externalAccounts?.[0]?.provider || 'email';
      const signedUpWithName = !!user.first_name;
      const isGuest = this.state.sharedWithMeTenants.length > 0;
      const isTeamMember = this.state.tenants?.length > 0;
      const signupType = isGuest ? 'guest' : isTeamMember ? 'team-member' : 'normal';

      this.analyticsService.track('signup', true, {
        provider,
        signedUpWithName,
        signupType,
      });
    }
    lastValueFrom(this.http.patch(`${this.config.apiEndpoint}/profile`, { questionnaire: user.questionnaire }));
  }

  private checkClickParams(user: UserDto) {
    if (!user) return;
    if (this.state.impersonating) return;
    const newTrackParams = {
      ...this.analyticsService.getTrackingParams(),
      first_url: this.analyticsService.getFirstUrl(),
    };

    Object.keys(newTrackParams).forEach(key => {
      if (key === 'first_url') {
        // we don't ever want to override the first_url value if it is already set
        if (user.tracking_parameters.first_url) delete newTrackParams.first_url;
      }

      if (user.tracking_parameters[key] === newTrackParams[key]) {
        delete newTrackParams[key];
      }
    });
    if (!Object.values(newTrackParams).some(Boolean)) return;
    user.tracking_parameters = {
      ...user.tracking_parameters,
      ...newTrackParams,
    };
    lastValueFrom(
      this.http.patch(`${this.config.apiEndpoint}/profile`, { tracking_parameters: user.tracking_parameters })
    );
  }

  updateUser(data: UpdateProfileBodyDto): Observable<UserDto> {
    return this.http.patch<UserDto>(`${this.config.apiEndpoint}/profile`, data).pipe(
      tap(profile => {
        this.observableLoader.updateFuzzy<UserDto>('user-data_', user => {
          if (!user) return;
          const newUser = { ...user, ...profile };
          this.state.user = newUser; // TODO: remove state.user, use userService.getUser() instead
          this.state.user$.next(newUser);
          return newUser;
        });

        this.observableLoader.update<UserDto>('user_profile', user => {
          if (!data) return;
          const newUser: UserDto = { ...user, ...profile };
          return newUser;
        });

        this.observableLoader.remove('members');
      })
    );
  }

  private waitTillNameUpdated(firstName: string, lastName: string) {
    return of(undefined).pipe(
      tap(() => {
        this.observableLoader.removeFuzzy('user-data_');
        this.observableLoader.remove('user_profile');
      }),
      switchMap(() => this.getUser()),
      switchMap(user => {
        if (user.first_name === firstName && user.last_name === lastName) return of(user);
        return throwError(() => 'Name not updated');
      }),
      backoffRetry(),
      catchError(() => of(undefined))
    );
  }

  async updateName(first_name: string, last_name: string, waitTillUpdated = true) {
    await this.auth.clerk.user.update({ firstName: first_name, lastName: last_name });
    this.state.user.first_name = first_name;
    this.state.user.last_name = last_name;
    this.observableLoader.remove('user_profile');
    this.observableLoader.remove('members');
    if (waitTillUpdated) {
      await firstValueFrom(this.waitTillNameUpdated(first_name, last_name));
    }
  }

  changePassword(old_password: string, new_password: string) {
    return from(this.auth.clerk.user.updatePassword({ newPassword: new_password, currentPassword: old_password })).pipe(
      map(() => true),
      catchError(err => {
        const error = err.errors?.[0];
        if (!error?.longMessage) {
          this.toasts.add('Error changing password', 'error');
          return throwError(() => error);
        }
        this.toasts.add(error.longMessage, 'error');
        return of(false);
      })
    );
  }

  public getCannyToken() {
    return this.http
      .post<{ token: string }>(`${this.config.apiEndpoint}/auth/canny/token`, {})
      .pipe(map(resp => resp.token));
  }

  public isEnterpriseUser() {
    return this.getEnterpriseCompanyInfo().pipe(map(resp => !!resp));
  }

  public getEnterpriseCompanyInfo(): Observable<{
    name: string;
    image: string;
    domains: string[];
    limitInvitesToDomains: boolean;
  }> {
    const user = this.state.user;

    if (!user) return of(undefined);
    const domain = user.email.split('@')[1].toLowerCase();

    if (this.config.env === 'local' && user.email.includes('+enterprise')) {
      console.warn('Counting user as enterprise for local env');
      return of({
        name: 'Picflow',
        image: 'https://picflow.com/favicon.ico',
        domains: ['picflow.com'],
        limitInvitesToDomains: false,
      });
    }

    return this.observableLoader
      .get('enterpriseCompanyInfo_' + domain, () => {
        const cached = this.storage.get<{
          name: string;
          image: string;
          domains: string[];
          limitInvitesToDomains: boolean;
        }>('enterpriseCompanyInfo_' + domain);
        if (cached !== undefined) return of(cached);
        const env = this.browserService.getEnv();
        const baseUrl = env === 'local' ? 'https://dev.picflow.com' : this.browserService.getCorrectPicflowUrl();
        return this.http
          .get<{
            name: string;
            image: string;
            domains: string[];
            limitInvitesToDomains: boolean;
          }>(`${baseUrl}/internal-api/enterprise/${encodeURIComponent(domain)}`, {
            context: new HttpContext().set(IGNORE_ALL_ERRORS, true),
          })
          .pipe(
            catchError(() => of(false as const)),
            tap(resp => {
              this.storage.set('enterpriseCompanyInfo_' + domain, resp);
            })
          );
      })
      .pipe(map(resp => resp || undefined));
  }

  public getOrganizationInvites(): Observable<{ resource: UserOrganizationInvitationResource; tenant: TenantDto }[]> {
    return this.observableLoader.get('organizationInvites', () => {
      return from(this.auth.init()).pipe(
        switchMap(() => {
          if (!this.auth.clerk.user) return of([]);
          return from(this.auth.clerk.user.getOrganizationInvitations({ pageSize: 500, status: 'pending' })).pipe(
            map(resp => {
              return resp.data.filter(i => {
                return ![...this.state.tenants, ...this.state.sharedWithMeTenants].some(
                  t => t.clerk_organization_id === i.publicOrganizationData.id
                );
              });
            }),
            switchMap(orgs =>
              forkJoinSafe(
                orgs.map(resource =>
                  this.tenantService.getTenantFromOrgId(resource.publicOrganizationData.id).pipe(
                    take(1),
                    map(tenant => ({ resource, tenant }))
                  )
                )
              )
            )
          );
        })
      );
    });
  }

  public getPendingOrganizationInvites() {
    return this.getOrganizationInvites().pipe(
      map(invites => invites.filter(invite => invite.resource.status === 'pending'))
    );
  }

  public getOrganizationSuggestions(): Observable<{ resource: OrganizationSuggestionResource; tenant: TenantDto }[]> {
    return this.observableLoader.get('pendingOrganizationSuggestions', () => {
      return from(this.auth.init()).pipe(
        switchMap(() => {
          if (!this.auth.clerk.user) return of([]);
          return combineLatest([
            from(this.auth.clerk.user.getOrganizationSuggestions({ pageSize: 500 })),
            this.getOrganizationInvites(),
          ]).pipe(
            map(([resp, invitations]) => {
              return resp.data.filter(i => {
                if (
                  invitations.some(invite => invite.resource.publicOrganizationData.id === i.publicOrganizationData.id)
                ) {
                  return false;
                }
                return ![...this.state.tenants, ...this.state.sharedWithMeTenants].some(
                  t => t.clerk_organization_id === i.publicOrganizationData.id
                );
              });
            }),
            switchMap(orgs =>
              forkJoinSafe(
                orgs.map(resource =>
                  this.tenantService.getTenantFromOrgId(resource.publicOrganizationData.id).pipe(
                    take(1),
                    map(tenant => ({ resource, tenant }))
                  )
                )
              )
            )
          );
        })
      );
    });
  }

  public getPendingOrganizationSuggestions() {
    return this.getOrganizationSuggestions().pipe(
      map(suggestions =>
        suggestions.filter(suggestion => {
          return suggestion.resource.status === 'pending' || suggestion.resource.status === 'accepted';
        })
      )
    );
  }

  private async getOrCreateEmail(emailAddress: string) {
    const existing = this.auth.clerk.user.emailAddresses.find(e => e.emailAddress === emailAddress.toLowerCase());
    if (existing) return existing;
    try {
      const email = await this.auth.clerk.user.createEmailAddress({ email: emailAddress });
      return email;
    } catch (err) {
      console.error(err.errors[0]);
      return null;
    }
  }

  public async changeEmail(
    emailAddress: string
  ): Promise<'already_exists' | 'changed' | 'failed' | EmailAddressResource> {
    emailAddress = emailAddress.toLowerCase();
    const email = await this.getOrCreateEmail(emailAddress);
    if (!email) return 'already_exists';
    const status = email.verification.status;

    if (status === 'expired') {
      await email.destroy();
      return this.changeEmail(emailAddress);
    }

    if (status === 'verified') {
      const oldEmail = this.auth.clerk.user.emailAddresses.find(
        e => e.id === this.auth.clerk.user.primaryEmailAddressId
      );
      await this.auth.clerk.user.update({ primaryEmailAddressId: email.id });
      if (!oldEmail.linkedTo?.length) oldEmail.destroy();
      this.state.user.email = email.emailAddress;
      this.observableLoader.updateFuzzy<UserDto>('user-data_', user => {
        if (!user) return;
        user.email = email.emailAddress;
        return user;
      });
      return 'changed';
    }

    if (status === null) {
      return (await email.prepareVerification({
        strategy: 'email_code',
      })) as EmailAddressResource;
    }

    console.debug(email);
    await email.destroy();

    return 'failed';
  }

  public async deleteEmail(emailAddress: string) {
    const email = this.auth.clerk.user.emailAddresses.find(e => e.emailAddress === emailAddress);
    if (!email) return;
    if (email.linkedTo?.length) return;
    return email.destroy();
  }

  public async deleteUser() {
    await this.auth.clerk.user.delete();
    this.toasts.add('Account Deleted', 'success');
    await sleep(2000);
    this.browserService.reload(true);
  }

  public clearUserCache() {
    this.loader.remove('linkedAccounts');
    this.observableLoader.remove('pendingOrganizationInvites');
    this.observableLoader.removeFuzzy('myAssetWatcher-');
    this.observableLoader.removeFuzzy('myGalleryWatcher-');
  }
}
